import dynamic from 'next/dynamic';
import { Grid, Stack } from '@global-ecom/nitro-uds/elements';

import { useFeature } from 'hooks/useFeature';
import { useProductCustomization } from 'hooks/useProductCustomization';
import { useTranslate } from 'hooks/useTranslations';
import { useSticky } from 'hooks/useSticky';
import { PixleeProductWidget } from 'ui/components/PixleeProductWidget';
import { MyCustomizer } from 'ui/components/ProductCustomizers';
import { ProductStory } from 'ui/components/ProductStory';
import {
  AddToCartErrorMessage,
  ProductActions,
  ProductBreadcrumb,
  ProductDescription,
  ProductImageGallery,
  ProductInfo,
  ProductVariantSelectors,
  ShippingReturnsDetails,
  ShippingReturnsMessages,
} from 'ui/pages/ProductDetailsPages';
import { useSiteConfig } from 'hooks/useSiteConfig';
import { useInView } from 'hooks/useInView';
import { ExploreCollectionCta } from 'ui/components/ExploreCollectionCta';
import { SanityContentSlot } from 'ui/components/SanityContentSlot';
import ScheduledArrivalDate from 'ui/components/ScheduledArrivalDate';
import { MixedCartErrorMessage } from 'ui/components/MixedCartErrorMessage';
import { usePageEventsContext } from 'hooks/usePageEventsContext';
import { isAppExclusiveValidRange } from 'utils/isAppExclusiveValidRange';

import { PdpProps } from './ProductDetailsPage';

const KlarnaText = dynamic(() => import('ui/components/KlarnaText'), {
  ssr: false,
});

const PincodeServiceabilityCheck = dynamic(
  () => import('ui/components/PincodeServiceabilityCheck'),
  { ssr: false }
);

const ReviewsActions = dynamic(() => import('ui/components/ReviewsActions'));

export const PdpFullLayout = ({
  masterProduct,
  variation,
  inventory,
  content,
  productScore,
  isAppOnly,
  mixedCartNotAllowed,
  sizeHasChanged,
}: PdpProps) => {
  const reviewsEnabled = useFeature('RATINGS_AND_REVIEWS');
  const returnsDisabled = useFeature('PDP_DISABLE_RETURNS');
  const t = useTranslate();
  const { stickyRef, stickyTop } = useSticky(3);
  const { freeShippingThreshold } = useSiteConfig();
  const { customizationReceived, isCustomizingProduct } =
    useProductCustomization();
  const [pixleeInViewRef, pixleeInView] = useInView();
  const { pageviewEventHasFired } = usePageEventsContext();

  return (
    <>
      <ProductBreadcrumb />
      <Stack asChild gap={{ _: 'sm', sm: 'lg' }}>
        <section
          aria-labelledby="pdp-product-title"
          className="mobile:mt-4 tablet:mt-6 desktop:mt-8"
        >
          {variation && isCustomizingProduct ? (
            <MyCustomizer
              variation={variation}
              onProductCustomized={customizationReceived}
            />
          ) : (
            <Grid columns={{ _: '1', md: '8', lg: '12' }}>
              <ProductImageGallery />
              <section
                aria-label={t('productInfo')}
                className="order-1 h-full md:col-span-4"
              >
                <Stack
                  ref={stickyRef}
                  gap="base"
                  className={`md:pt-3 md:sticky md:top-[${stickyTop}px]`}
                >
                  <ProductInfo
                    rating={productScore?.rating}
                    reviewCount={productScore?.amount}
                  />
                  <KlarnaText
                    amount={variation?.salePrice || variation?.price}
                  />
                  <ExploreCollectionCta masterProduct={masterProduct} />
                  <ProductVariantSelectors
                    inventory={inventory?.data?.inventory.quantity}
                  />
                  <AddToCartErrorMessage />
                  {sizeHasChanged && (
                    <MixedCartErrorMessage
                      mixedCartNotAllowed={mixedCartNotAllowed!}
                    />
                  )}
                  {variation?.displayOutOfStock?.presale &&
                    variation?.displayOutOfStock?.validTo && (
                      <ScheduledArrivalDate
                        preOrderDeliveryDate={
                          variation.displayOutOfStock.validTo
                        }
                      />
                    )}
                  <ProductActions
                    inventory={inventory?.data?.inventory.quantity}
                    mixedCartNotAllowed={mixedCartNotAllowed}
                  />
                  {!isAppExclusiveValidRange(
                    isAppOnly,
                    variation?.appOnlyDateTimeFrom,
                    variation?.appOnlyDateTimeTo
                  ) && (
                    <ShippingReturnsMessages
                      freeShippingThreshold={freeShippingThreshold}
                      variation={variation}
                      returnsDisabled={returnsDisabled}
                    />
                  )}
                  <PincodeServiceabilityCheck />
                  <ProductDescription />
                  {reviewsEnabled && (
                    <ReviewsActions
                      productId={masterProduct?.id}
                      variationId={variation?.id}
                      productName={masterProduct?.name}
                      rating={productScore?.rating}
                      amount={productScore?.amount as string}
                      pageviewEventHasFired={pageviewEventHasFired}
                    />
                  )}
                  {!isAppExclusiveValidRange(
                    isAppOnly,
                    variation?.appOnlyDateTimeFrom,
                    variation?.appOnlyDateTimeTo
                  ) && (
                    <ShippingReturnsDetails
                      freeShippingThreshold={freeShippingThreshold}
                      callout={content?.pdpCallout}
                    />
                  )}
                </Stack>
              </section>
            </Grid>
          )}
          <SanityContentSlot
            items={content?.aboveProductStory}
            slotId="aboveProductStory"
            documentId={masterProduct?.id}
          />
          {variation?.productStory && (
            <ProductStory
              {...variation.productStory}
              additionalContentSections={
                content?.ProductStoryAdditionalContentSlot
              }
              documentId={content?._id}
              slotId="ProductStoryAdditionalContentSlot"
            />
          )}
          {masterProduct?.id && (
            <div ref={pixleeInViewRef as React.RefObject<HTMLDivElement>}>
              {pixleeInView && (
                <PixleeProductWidget
                  key={variation?.id}
                  skuId={masterProduct.id}
                />
              )}
            </div>
          )}
          <SanityContentSlot
            items={content?.belowProductStory}
            slotId="belowProductStory"
            documentId={content?._id}
          />
        </section>
      </Stack>
    </>
  );
};
